import { Alert } from '@/global/components/Alert'
import IconButton from '@/global/components/Button/IconButton'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import Grid from '@/global/components/ui/Grid'
import dayjs from 'dayjs'
import { FormDatePicker, FormFieldSelect } from 'global/components/Form'
import { SelectFuncionario } from 'global/components/Selects'
import { ArrowRightLeftIcon, XIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { tabelaHorarios, TrocaDiaTrabalho } from 'service/api'

interface IProps {
    /** Desabilita o SelectEmpresa e SelectFuncionario */
    disableSelects?: boolean
    /** Desabilita o botão alterar dia */
    disabled?: boolean
}

export function FormTrocaDiaTrabalho({ disableSelects, disabled }: IProps) {
    const { watch, setValue, getValues } = useFormContext<TrocaDiaTrabalho>()

    const { tabelaHorarioId, trabalha, folga } = watch()

    const { data = [] } = tabelaHorarios.useFetch()

    return (
        <Grid container>
            <Grid item xs={12}>
                <SelectFuncionario variant="form" disabled={disableSelects} />
            </Grid>
            <HorizontalDivider className="col-span-12 mt-4 mb-3 select-none">
                Detalhes da troca
            </HorizontalDivider>
            <Grid item xs={5}>
                <FormDatePicker
                    type="date"
                    name="trabalha"
                    label="Dia de trabalho"
                    required
                    startAdornment={
                        trabalha ? (
                            <span>
                                {dayjs.utc(trabalha).format('ddd')},&nbsp;
                            </span>
                        ) : null
                    }
                />
            </Grid>
            <Grid item xs={2} className="flex justify-center items-center mt-2">
                <IconButton
                    title="Trocar dias"
                    onClick={() => {
                        const trabalha = getValues('trabalha')
                        const folga = getValues('folga')
                        setValue('trabalha', folga)
                        setValue('folga', trabalha)
                    }}
                    className="mb-5"
                    disabled={disabled}
                >
                    <ArrowRightLeftIcon className="size-8" />
                </IconButton>
            </Grid>
            <Grid item xs={5}>
                <FormDatePicker
                    type="date"
                    name="folga"
                    label="Dia da folga"
                    required
                    startAdornment={
                        folga ? (
                            <span>{dayjs.utc(folga).format('ddd')},&nbsp;</span>
                        ) : null
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <FormFieldSelect
                    name="tabelaHorarioId"
                    label="Trocar tabela no dia de trabalho por"
                    placeholder="Tabela do dia de folga"
                    // `null`, e não `''`: o campo é opcional na API, e o
                    // `@IsOptional` do class-validator só ignora `null` e
                    // `undefined` — string vazia cai no `@IsNumber` e reprova.
                    defaultValue={null}
                    options={[
                        {
                            value: null,
                            label: <em>Tabela do dia de folga</em>,
                        },
                        ...data.map(({ id, nome }) => ({
                            value: id,
                            label: nome,
                        })),
                    ]}
                    endAdornment={
                        tabelaHorarioId ? (
                            <IconButton
                                title="Remover"
                                onClick={() =>
                                    setValue('tabelaHorarioId', null)
                                }
                            >
                                <XIcon />
                            </IconButton>
                        ) : null
                    }
                />
            </Grid>
            <Grid item xs={12}>
                <Alert severity="info">
                    <span className="font-bold">Resumo da troca:</span>
                    <ul className="pl-4">
                        <li>
                            No dia{' '}
                            <b>
                                {trabalha
                                    ? dayjs
                                          .utc(trabalha)
                                          .format('DD/MM/YYYY')
                                          .concat(
                                              ' ' +
                                                  dayjs
                                                      .utc(trabalha)
                                                      .format('(dddd)'),
                                          )
                                    : 'ainda não definido'}
                            </b>{' '}
                            o funcionário irá trabalhar (em vez de folgar) e
                            cumprir a tabela{' '}
                            <b>
                                {tabelaHorarioId
                                    ? data.find(t => t.id === tabelaHorarioId)
                                          ?.nome || 'N/A'
                                    : 'originalmente do dia '.concat(
                                          folga
                                              ? dayjs
                                                    .utc(folga)
                                                    .format('DD [de] MMMM')
                                              : 'de folga',
                                      )}
                            </b>
                            .
                        </li>
                        <li>
                            No dia{' '}
                            <b>
                                {folga
                                    ? dayjs
                                          .utc(folga)
                                          .format('DD/MM/YYYY')
                                          .concat(
                                              ' ' +
                                                  dayjs
                                                      .utc(folga)
                                                      .format('(dddd)'),
                                          )
                                    : 'ainda não definido'}
                            </b>{' '}
                            o funcionário irá folgar (em vez de trabalhar).
                        </li>
                    </ul>
                </Alert>
            </Grid>
        </Grid>
    )
}
